<template>
  <div class="upload-config-container">
    <Card>
      <p slot="title">上传设置管理</p>

      <div class="search-con">
        <Input
          v-model="searchParams.config_name"
          placeholder="请输入配置名称"
          style="width: 300px; margin-right: 10px"
          clearable
          @on-enter="handleSearch"
        >
          <span slot="prepend">配置名称</span>
        </Input>

        <Input
          v-model="searchParams.related_table"
          placeholder="请输入关联表名"
          style="width: 200px; margin-right: 10px"
          clearable
        >
          <span slot="prepend">关联表名</span>
        </Input>

        <Button type="primary" @click="handleSearch">搜索</Button>
        <Button type="success" @click="handleAdd" style="margin-left: 10px">新增配置</Button>
      </div>

      <div style="margin-top: 20px">
        <Table
          :columns="columns"
          :data="tableData"
          :loading="loading"
          border
          stripe
          @on-sort-change="handleSortChange"
        >
          <template slot-scope="{ row }" slot="action">
            <Button type="primary" size="small" @click="handleEdit(row)">编辑</Button>
            <Button
              type="warning"
              size="small"
              style="margin-left: 10px"
              @click="handleStatus(row)"
            >
              {{ row.is_active === 1 ? '禁用' : '启用' }}
            </Button>
            <Button
              type="error"
              size="small"
              style="margin-left: 10px"
              @click="handleDelete(row)"
            >
              删除
            </Button>
          </template>

          <template slot-scope="{ row }" slot="is_active">
            <Tag :type="row.is_active === 1 ? 'success' : 'error'">
              {{ row.is_active === 1 ? '启用' : '禁用' }}
            </Tag>
          </template>

          <template slot-scope="{ row }" slot="allowed_file_types">
            <div v-if="row.allowed_file_types">
              <Tag
                v-for="(type, index) in parseFileTypes(row.allowed_file_types)"
                :key="index"
                type="info"
                style="margin-right: 5px; margin-bottom: 5px"
              >
                {{ type }}
              </Tag>
            </div>
          </template>
        </Table>

        <div style="margin-top: 20px; text-align: right">
          <Page
            :total="total"
            :current="pageParams.page"
            :page-size="pageParams.limit"
            @on-change="handlePageChange"
            @on-page-size-change="handlePageSizeChange"
            show-sizer
            show-total
          />
        </div>
      </div>
    </Card>

    <!-- 新增/编辑弹窗 -->
    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      width="700px"
      @on-ok="handleSubmit"
      @on-cancel="modalVisible = false"
    >
      <Form ref="uploadForm" :model="formData" :rules="formRules">
        <FormItem label="配置名称" prop="config_name">
          <Input v-model="formData.config_name" placeholder="请输入配置名称" />
        </FormItem>

        <FormItem label="允许文件类型" prop="allowed_file_types">
          <Select
            v-model="formData.allowed_file_types"
            multiple
            filterable
            placeholder="请选择允许的文件类型"
            style="width: 100%"
          >
            <Option v-for="type in allowedFileTypes" :key="type" :value="type">{{ type }}</Option>
          </Select>
          <div style="margin-top: 5px; color: #888; font-size: 12px">
            支持的文件类型：{{ allowedFileTypes.join(', ') }}
          </div>
        </FormItem>

        <FormItem label="文件保存路径" prop="file_save_path">
          <Input v-model="formData.file_save_path" placeholder="请输入文件保存路径" />
        </FormItem>

        <FormItem label="返回路径前缀" prop="return_path_prefix">
          <Input v-model="formData.return_path_prefix" placeholder="请输入返回路径前缀" />
        </FormItem>

        <FormItem label="关联数据表" prop="related_table">
          <Input v-model="formData.related_table" placeholder="请输入关联数据表名" />
        </FormItem>

        <FormItem label="状态" prop="is_active">
          <Select v-model="formData.is_active">
            <Option value="1">启用</Option>
            <Option value="0">禁用</Option>
          </Select>
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="modalVisible = false">取消</Button>
        <Button type="primary" @click="handleSubmit">确定</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import { getUploadConfigList, operationUploadConfig, deleteUploadConfig } from '@/api/system/upload_config'

export default {
  name: 'UploadConfig',
  data () {
    return {
      loading: false,
      tableData: [],
      total: 0,

      searchParams: {
        config_name: '',
        related_table: ''
      },

      pageParams: {
        page: 1,
        limit: 10
      },

      modalVisible: false,
      modalTitle: '',
      formData: {
        id: null,
        config_name: '',
        allowed_file_types: [],
        file_save_path: '',
        return_path_prefix: '',
        related_table: '',
        is_active: 1
      },

      formRules: {
        config_name: [
          { required: true, message: '请输入配置名称', trigger: 'blur' },
          { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        allowed_file_types: [
          {
            required: true,
            message: '请选择允许的文件类型',
            trigger: 'change',
            validator: (rule, value, callback) => {
              if (value && value.length > 0) {
                callback()
              } else {
                callback(new Error('请选择允许的文件类型'))
              }
            }
          }
        ],
        file_save_path: [
          { required: true, message: '请输入文件保存路径', trigger: 'blur' },
          { pattern: /^.*\/$/, message: '路径格式应为以/开头和结尾，如 /upload/files/', trigger: 'blur' }
        ],
        return_path_prefix: [
          { required: true, message: '请输入返回路径前缀', trigger: 'blur' },
          { pattern: /^\/.*\/$/, message: '路径格式应为以/开头和结尾，如 /static/upload/files/', trigger: 'blur' }
        ],
        related_table: [
          { max: 100, message: '长度不能超过 100 个字符', trigger: 'blur' }
        ],
        is_active: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      },

      columns: [
        { title: 'ID', key: 'id', width: 80, sortable: true },
        { title: '配置名称', key: 'config_name', width: 180, sortable: true },
        { title: '允许文件类型', slot: 'allowed_file_types', minWidth: 200 },
        { title: '文件保存路径', key: 'file_save_path', width: 200 },
        { title: '返回路径前缀', key: 'return_path_prefix', width: 200 },
        { title: '关联表名', key: 'related_table', width: 150 },
        { title: '状态', slot: 'is_active', width: 100 },
        { title: '创建时间', key: 'create_time', width: 180, sortable: true },
        { title: '更新时间', key: 'update_time', width: 180, sortable: true },
        { title: '操作', slot: 'action', width: 250, fixed: 'right' }
      ],

      allowedFileTypes: [
        'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp',
        'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt',
        'zip', 'rar', '7z', 'tar', 'gz'
      ]
    }
  },

  mounted () {
    this.getConfigList()
  },

  methods: {
    async getConfigList () {
      this.loading = true
      try {
        const res = await getUploadConfigList(
          this.searchParams.config_name,
          this.searchParams.related_table,
          this.pageParams.page,
          this.pageParams.limit
        )

        if (res.data.code === 0) {
          this.tableData = res.data.data || []
          this.total = res.data.count || 0
        } else {
          this.$Message.error(res.data.msg || '获取上传配置列表失败')
        }
      } catch (error) {
        this.$Message.error('获取上传配置列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    handleSearch () {
      this.pageParams.page = 1
      this.getConfigList()
    },

    handleSortChange ({ column, key, order }) {
      // 排序功能实现
      console.log('排序:', column, key, order)
    },

    handlePageChange (page) {
      this.pageParams.page = page
      this.getConfigList()
    },

    handlePageSizeChange (pageSize) {
      this.pageParams.limit = pageSize
      this.pageParams.page = 1
      this.getConfigList()
    },

    handleAdd () {
      this.modalTitle = '新增上传配置'
      this.formData = {
        id: null,
        config_name: '',
        allowed_file_types: [],
        file_save_path: '',
        return_path_prefix: '',
        related_table: '',
        is_active: 1
      }
      this.modalVisible = true
    },

    handleEdit (row) {
      this.modalTitle = '编辑上传配置'
      this.formData = {
        ...row,
        allowed_file_types: this.parseFileTypes(row.allowed_file_types)
      }
      this.modalVisible = true
    },

    handleStatus (row) {
      this.$Modal.confirm({
        title: '确认操作',
        content: `确定要${row.is_active === 1 ? '禁用' : '启用'}该上传配置吗？`,
        onOk: async () => {
          try {
            const res = await operationUploadConfig({
              ...row,
              is_active: row.is_active === 1 ? 0 : 1
            }, 'post')

            if (res.data.code === 0) {
              this.$Message.success('操作成功')
              this.getConfigList()
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          } catch (error) {
            this.$Message.error('操作失败')
            console.error(error)
          }
        }
      })
    },

    handleDelete (row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: '确定要删除该上传配置吗？',
        onOk: async () => {
          try {
            const res = await deleteUploadConfig({ id: row.id })

            if (res.data.code === 0) {
              this.$Message.success('删除成功')
              this.getConfigList()
            } else {
              this.$Message.error(res.data.msg || '删除失败')
            }
          } catch (error) {
            this.$Message.error('删除失败')
            console.error(error)
          }
        }
      })
    },

    parseFileTypes (types) {
      if (typeof types === 'string') {
        try {
          return JSON.parse(types)
        } catch (error) {
          return types.split(',').map(t => t.trim()).filter(t => t)
        }
      }
      return Array.isArray(types) ? types : []
    },

    async handleSubmit () {
      try {
        this.$refs.uploadForm.validate(async (valid) => {
          if (valid) {
            // 处理文件类型数组转为JSON字符串
            const submitData = {
              ...this.formData,
              allowed_file_types: JSON.stringify(this.formData.allowed_file_types)
            }

            let res
            if (this.formData.id) {
              res = await operationUploadConfig(submitData, 'post')
            } else {
              res = await operationUploadConfig(submitData, 'post')
            }

            if (res.data.code === 0) {
              this.$Message.success(this.formData.id ? '更新成功' : '新增成功')
              this.modalVisible = false
              this.getConfigList()
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          }
        })
      } catch (error) {
        this.$Message.error('操作失败')
        console.error(error)
      }
    }
  }
}
</script>

<style scoped>
.upload-config-container {
  padding: 10px;
}

.search-con {
  display: flex;
  align-items: center;
}
</style>
